import { useState, useMemo } from 'react'
import { MdEdit, MdDelete } from 'react-icons/md'
import { MainPage } from 'components/templates/MainPage'
import { useFocusDeals } from 'api/company/useFocusDeals'
import { insertFocusDeal, updateFocusDeal, deleteFocusDeal, FocusDealPayload } from 'api/company/focusDealApi'
import { useAllCompany } from 'api/company/useAllCompany'
import swal from 'sweetalert2'

type FilterState = {
  companyId: number | undefined
  jobType: 'RA' | 'CA' | undefined
}

const formatAmount = (v: string | null) => {
  if (v == null || v === '') return '-'
  const num = Number(v.replace(/,/g, ''))
  return isNaN(num) ? v : `¥${num.toLocaleString()}`
}

// CSV export helper
const exportCsv = (deals: ReturnType<typeof useFocusDeals>['data']) => {
  const header = ['ID', 'タイトル', '内容', '見込み売り上げ', '見込み利益', '職種', '会社名']
  const rows = deals.map((d) => [
    d.id,
    d.title,
    d.content ?? '',
    d.expectedSales ?? '',
    d.expectedProfit ?? '',
    d.jobType,
    d.company?.name ?? '',
  ])
  const csv = [header, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n')
  const bom = '﻿'
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = '注力取引.csv'
  a.click()
  URL.revokeObjectURL(url)
}

type ModalState = {
  open: boolean
  deal?: ReturnType<typeof useFocusDeals>['data'][0]
}

const DealModal = ({
  deal,
  companies,
  onClose,
  onSave,
}: {
  deal?: ReturnType<typeof useFocusDeals>['data'][0]
  companies: { id: number; name: string }[]
  onClose: () => void
  onSave: (payload: FocusDealPayload) => Promise<void>
}) => {
  const [title, setTitle] = useState(deal?.title ?? '')
  const [content, setContent] = useState(deal?.content ?? '')
  const formatInput = (v: string | null | undefined) => {
    if (v == null || v === '') return ''
    const num = Number(v.replace(/,/g, ''))
    return isNaN(num) ? v : num.toLocaleString()
  }

  const [expectedSales, setExpectedSales] = useState(formatInput(deal?.expectedSales))
  const [expectedProfit, setExpectedProfit] = useState(formatInput(deal?.expectedProfit))
  const [jobType, setJobType] = useState<'RA' | 'CA'>(deal?.jobType ?? 'RA')
  const [companyId, setCompanyId] = useState<number | ''>(deal?.companyId ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    if (!title.trim()) { setError('タイトルは必須です。'); return }
    if (!companyId) { setError('会社は必須です。'); return }
    setSaving(true)
    try {
      await onSave({
        title: title.trim(),
        content: content || undefined,
        expectedSales: expectedSales ? expectedSales.replace(/,/g, '') : undefined,
        expectedProfit: expectedProfit ? expectedProfit.replace(/,/g, '') : undefined,
        jobType,
        companyId: Number(companyId),
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h2 className="mb-5 text-lg font-bold text-gray-800">
          {deal ? '注力取引を編集' : '注力取引を追加'}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">タイトル <span className="text-red-500">*</span></label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#01695C]" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">内容</label>
            <textarea value={content ?? ''} onChange={(e) => setContent(e.target.value)} rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#01695C]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">見込み売り上げ（円）</label>
              <input
                type="text"
                inputMode="numeric"
                value={expectedSales}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '')
                  setExpectedSales(raw ? Number(raw).toLocaleString() : '')
                }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#01695C]"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">見込み利益（円）</label>
              <input
                type="text"
                inputMode="numeric"
                value={expectedProfit}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '')
                  setExpectedProfit(raw ? Number(raw).toLocaleString() : '')
                }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#01695C]"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">職種 <span className="text-red-500">*</span></label>
            <select value={jobType} onChange={(e) => setJobType(e.target.value as 'RA' | 'CA')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#01695C]">
              <option value="RA">RA</option>
              <option value="CA">CA</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">会社 <span className="text-red-500">*</span></label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : '')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#01695C]">
              <option value="">選択してください</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            キャンセル
          </button>
          <button type="button" onClick={handleSubmit} disabled={saving}
            className="rounded-lg bg-[#01695C] px-4 py-2 text-sm text-white hover:opacity-90 disabled:opacity-60">
            {saving ? '保存中...' : deal ? '更新' : '追加'}
          </button>
        </div>
      </div>
    </div>
  )
}

export const ProjectsTalentsPage = (): JSX.Element => {
  const [filter, setFilter] = useState<FilterState>({ companyId: undefined, jobType: undefined })
  const { data: deals, mutate } = useFocusDeals(filter)
  const { data: companiesData } = useAllCompany()
  const [modal, setModal] = useState<ModalState>({ open: false })

  const companies = useMemo(
    () => (companiesData ?? []).map((c) => ({ id: c.id, name: c.name })),
    [companiesData]
  )

  const handleSave = async (payload: FocusDealPayload) => {
    try {
      if (modal.deal) {
        await updateFocusDeal(modal.deal.id, payload)
      } else {
        await insertFocusDeal(payload)
      }
      await mutate()
      setModal({ open: false })
    } catch {
      swal.fire({ icon: 'error', text: '保存に失敗しました。', confirmButtonText: 'OK' })
    }
  }

  const handleDelete = async (id: number) => {
    const result = await swal.fire({
      icon: 'warning',
      text: 'この取引を削除しますか？',
      showCancelButton: true,
      confirmButtonText: '削除',
      cancelButtonText: 'キャンセル',
      confirmButtonColor: '#ef4444',
    })
    if (!result.isConfirmed) return
    try {
      await deleteFocusDeal(id)
      await mutate()
    } catch {
      swal.fire({ icon: 'error', text: '削除に失敗しました。', confirmButtonText: 'OK' })
    }
  }

  return (
    <MainPage>
      <>
      <div className="space-y-5">
        {/* Filters + Actions */}
        <div className="flex items-center gap-3">
          <select
            value={filter.jobType ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, jobType: (e.target.value as 'RA' | 'CA') || undefined }))}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#01695C]"
          >
            <option value="">すべての職種</option>
            <option value="RA">RA</option>
            <option value="CA">CA</option>
          </select>
          <select
            value={filter.companyId ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, companyId: e.target.value ? Number(e.target.value) : undefined }))}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#01695C]"
          >
            <option value="">すべての会社</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => exportCsv(deals)}
              className="rounded border border-[#ddd] bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              CSV エクスポート
            </button>
            <button
              type="button"
              onClick={() => setModal({ open: true })}
              className="rounded bg-[#01695C] px-4 py-2 text-sm text-white hover:opacity-90"
            >
              + 注力取引を追加
            </button>
          </div>
        </div>

        {/* Deal List */}
        {deals.length === 0 ? (
          <p className="py-16 text-center text-sm text-gray-400">注力取引はありません。</p>
        ) : (
          <div className="space-y-3">
            {deals.map((deal) => (
              <div key={deal.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-2">
                    {/* 1行目: 職種 + タイトル */}
                    <div className="flex items-center gap-2">
                      <span className="rounded bg-[#01695C] px-2 py-0.5 text-xs font-bold text-white">
                        {deal.jobType}
                      </span>
                      <span className="text-sm font-semibold text-gray-800">{deal.title}</span>
                    </div>
                    {/* 2行目: 見込み売り上げ・見込み利益 */}
                    <div className="flex gap-6 text-sm">
                      <div>
                        <span className="text-xs text-gray-400">見込み売り上げ</span>
                        <p className="font-medium text-gray-800">{formatAmount(deal.expectedSales)}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">見込み利益</span>
                        <p className="font-medium text-gray-800">{formatAmount(deal.expectedProfit)}</p>
                      </div>
                    </div>
                    {/* 3行目: 内容（URL自動リンク） */}
                    {deal.content && (
                      <p className="text-sm text-gray-600 break-all whitespace-pre-wrap">
                        {deal.content.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
                          /^https?:\/\//.test(part) ? (
                            <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-[#01695C] hover:underline">
                              {part}
                            </a>
                          ) : (
                            part
                          )
                        )}
                      </p>
                    )}
                  </div>
                  <div className="ml-4 flex gap-1">
                    <button
                      type="button"
                      onClick={() => setModal({ open: true, deal })}
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-[#01695C]"
                    >
                      <MdEdit size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(deal.id)}
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal.open && (
        <DealModal
          deal={modal.deal}
          companies={companies}
          onClose={() => setModal({ open: false })}
          onSave={handleSave}
        />
      )}
      </>
    </MainPage>
  )
}
